import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Loader2, Search, Globe } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STAGE_LABELS } from "@/lib/constants";
import { useT } from "@/i18n/I18nProvider";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const EMPTY = {
  first_name: "", last_name: "", date_of_birth: "", gender: "", phone: "",
  email: "", country: "", language: "", notes: "",
};

export default function Patients() {
  const { can } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/patients").then((r) => setPatients(r.data)).finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/patients", form);
      toast.success(`${t("patients.registered")} · ${data.patient_code}`);
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const filtered = patients.filter((p) =>
    `${p.first_name} ${p.last_name} ${p.patient_code} ${p.country}`.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]">
      <PageHeader title={t("patients.title")} subtitle={t("patients.subtitle")}>
        {can("patient_records", "create") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="register-patient-button" className="bg-navy hover:bg-navy-soft text-white gap-2">
                <UserPlus className="h-4 w-4" /> {t("patients.register")}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">{t("patients.registerNew")}</DialogTitle>
                <DialogDescription>{t("patients.registerDesc")}</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-4 mt-2" data-testid="patient-form">
                <div className="space-y-1.5">
                  <Label>{t("patients.firstName")} *</Label>
                  <Input data-testid="patient-first-name" value={form.first_name} onChange={set("first_name")} required />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("patients.lastName")} *</Label>
                  <Input data-testid="patient-last-name" value={form.last_name} onChange={set("last_name")} required />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("patients.dob")}</Label>
                  <Input type="date" value={form.date_of_birth} onChange={set("date_of_birth")} />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("patients.gender")}</Label>
                  <Input value={form.gender} onChange={set("gender")} placeholder="male / female" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("patients.phone")}</Label>
                  <Input value={form.phone} onChange={set("phone")} />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("patients.email")}</Label>
                  <Input type="email" value={form.email} onChange={set("email")} />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("patients.country")}</Label>
                  <Input value={form.country} onChange={set("country")} />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("patients.patientLanguage")}</Label>
                  <Input value={form.language} onChange={set("language")} />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label>{t("patients.notes")}</Label>
                  <Input value={form.notes} onChange={set("notes")} />
                </div>
                <DialogFooter className="col-span-2 mt-2">
                  <Button type="submit" data-testid="patient-submit" disabled={saving} className="bg-navy hover:bg-navy-soft text-white">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("patients.register")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          data-testid="patient-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("patients.searchPlaceholder")}
          className="pl-9"
        />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>{t("patients.code")}</TableHead>
                <TableHead>{t("patients.patientName")}</TableHead>
                <TableHead>{t("patients.country")}</TableHead>
                <TableHead>{t("patients.patientLanguage")}</TableHead>
                <TableHead>{t("patients.journeyStage")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow
                  key={p.id}
                  data-testid={`patient-row-${p.patient_code}`}
                  onClick={() => navigate(`/patients/${p.id}`)}
                  className="cursor-pointer hover:bg-slate-50"
                >
                  <TableCell className="font-mono text-sm font-medium text-navy">{p.patient_code}</TableCell>
                  <TableCell className="font-medium">{p.first_name} {p.last_name}</TableCell>
                  <TableCell className="text-slate-600">
                    <span className="inline-flex items-center gap-1.5"><Globe className="h-3.5 w-3.5 text-slate-400" />{p.country || "—"}</span>
                  </TableCell>
                  <TableCell className="text-slate-600">{p.language || "—"}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="font-normal">{t(`stages.${p.journey_stage}`, STAGE_LABELS[p.journey_stage] || p.journey_stage)}</Badge>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400">{t("patients.notFound")}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
